import * as React from "react";
import { Transition, TransitionChild } from "@headlessui/react";
import { cn } from "~/lib/utils";
import ReactDOM from "react-dom";

// // // //

function ModalPortal(props: { children: React.ReactNode }) {
    const { children } = props;

    // Disable in SSR runtime
    if (typeof document === "undefined") {
        return null;
    }

    const portalNode = document.getElementById("modal-portal-container");
    if (!portalNode) {
        return null;
    }
    return ReactDOM.createPortal(children, portalNode);
}

export function Modal(props: {
    children: React.ReactNode;
    show: boolean;
    allowClose?: boolean;
    size?: "sm" | "md" | "lg";
    onHide?: () => void;
    allowScroll?: boolean;
    blurBackground?: boolean;
    viewport?: "fullscreen" | "inset";
}) {
    const {
        show,
        allowClose = true,
        size = "md",
        allowScroll = true,
        blurBackground = false,
        viewport: blurBackgroundScope = "fullscreen",
    } = props;

    const modalRootClassName = cn("fixed z-500 overflow-y-auto", {
        "inset-0": blurBackgroundScope === "fullscreen",
        "bottom-0 left-0 right-0 top-[var(--app-mobile-nav-height)] md:left-[var(--app-sidebar-current-width)] md:top-[var(--app-nav-height)]":
            blurBackgroundScope === "inset",
    });
    const modalContentClassName = cn("text-center", {
        "flex items-end justify-center min-h-screen pt-4 px-4 pb-20 sm:block sm:p-0":
            blurBackgroundScope === "fullscreen",
        "flex min-h-full items-center justify-center px-4 py-6 sm:p-8":
            blurBackgroundScope === "inset",
    });

    // Handle body overflow (to prevent scrolling when modal is shown)
    React.useEffect(() => {
        if (allowScroll) {
            return;
        }

        if (show) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        // Cleanup the class when the component is unmounted or when the modal is hidden
        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [allowScroll, show]);

    return (
        <ModalPortal>
            <Transition.Root show={show} as={React.Fragment}>
                <div className={modalRootClassName}>
                    <div className={modalContentClassName}>
                        <TransitionChild
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div
                                className={cn(
                                    "absolute inset-0 bg-slate-700/20 transition-opacity",
                                    {
                                        "backdrop-blur": blurBackground,
                                    },
                                )}
                                role={allowClose ? "button" : undefined}
                                tabIndex={-1}
                                onClick={() => {
                                    if (allowClose && props.onHide) {
                                        props.onHide();
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "esc" && props.onHide) {
                                        props.onHide();
                                    }
                                }}
                            />
                        </TransitionChild>

                        {/* <!-- This element is to trick the browser into centering the modal contents. --> */}
                        {blurBackgroundScope === "fullscreen" && (
                            <span
                                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                                aria-hidden="true"
                            >
                                &#8203;
                            </span>
                        )}

                        {/* Modal panel, show/hide based on modal state. */}
                        <TransitionChild
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div
                                className={cn(
                                    "relative z-10 inline-block align-bottom rounded text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle",
                                    {
                                        "sm:max-w-lg sm:w-full": size === "md",
                                        "max-w-4xl w-full": size === "lg",
                                    },
                                )}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="modal-headline"
                            >
                                {props.children}
                            </div>
                        </TransitionChild>
                    </div>
                </div>
            </Transition.Root>
        </ModalPortal>
    );
}
